/**
 * Dragging a kanban card to another column, driven by the pointer.
 *
 * HTML5 drag and drop cannot work in the desktop app: Tauri's native file
 * drop handler answers every drag over the webview itself, so `dragover` and
 * `drop` never reach the page (see `doc-frame/use-doc-block-drag.ts`). A
 * press that moves a few pixels becomes a drag instead: a copy of the card
 * follows the pointer, the column under it (`data-kanban-status`) is the
 * target, and the release drops the task there; near the board's left or
 * right edge the drag scrolls it. A press without a move stays a click;
 * Escape or a release outside every column cancels.
 */
import { useEffect, useRef, type PointerEvent } from "react";
import { cn } from "@/lib/cn";
import type { TaskStatus } from "@/api/tasks";

/** How far the pointer moves before a press becomes a drag. */
const DRAG_THRESHOLD_PX = 4;
/** How close to the board's edge a drag scrolls it, and by how much. */
const EDGE_SCROLL_PX = 32;
const EDGE_SCROLL_STEP_PX = 12;

/** The attribute naming the status of the column a card is dropped on. */
const KANBAN_STATUS_ATTRIBUTE = "data-kanban-status";

/** Controls inside a card keep their own press (the delegation chip). */
const NESTED_CONTROLS = "button,a,input,textarea,select";

type Point = { clientX: number; clientY: number };

export interface KanbanCardDragHandlers {
  /** The press became a drag. */
  onDragStart: () => void;
  /** The column under the pointer changed (`null` over no column). */
  onDragOver: (status: TaskStatus | null) => void;
  /** The drag ended over `status`, or was cancelled (`null`). */
  onDragEnd: (status: TaskStatus | null) => void;
}

export interface KanbanCardDrag {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  /** True once after a drag ended: the click that follows is not a click. */
  consumeClick: () => boolean;
}

function statusAt(doc: Document, point: Point): TaskStatus | null {
  const column = doc
    .elementFromPoint(point.clientX, point.clientY)
    ?.closest<HTMLElement>(`[${KANBAN_STATUS_ATTRIBUTE}]`);
  return (column?.getAttribute(KANBAN_STATUS_ATTRIBUTE) as TaskStatus) ?? null;
}

/** Near the board's left or right edge, a drag scrolls it sideways. */
function scrollBoardAtEdge(card: HTMLElement, point: Point) {
  const board = card.closest<HTMLElement>("[data-kanban-board]");
  if (!board) return;
  const { left, right } = board.getBoundingClientRect();
  if (point.clientX < left + EDGE_SCROLL_PX) {
    board.scrollLeft -= EDGE_SCROLL_STEP_PX;
  } else if (point.clientX > right - EDGE_SCROLL_PX) {
    board.scrollLeft += EDGE_SCROLL_STEP_PX;
  }
}

/**
 * The card's copy under the pointer: outside the column's scroll clip, with
 * no ids or test ids, so lookups by task id still find only the real card.
 * It keeps the card's layout size and takes the canvas zoom as a scale.
 */
function createGhost(card: HTMLElement): { ghost: HTMLElement; scale: number } {
  const rect = card.getBoundingClientRect();
  const scale = card.offsetWidth > 0 ? rect.width / card.offsetWidth : 1;
  const ghost = card.cloneNode(true) as HTMLElement;
  for (const element of [ghost, ...ghost.querySelectorAll("*")]) {
    for (const name of element.getAttributeNames()) {
      if (name.startsWith("data-") || name === "id") {
        element.removeAttribute(name);
      }
    }
  }
  ghost.removeAttribute("role");
  ghost.removeAttribute("tabindex");
  ghost.setAttribute("aria-hidden", "true");
  ghost.className = cn(
    card.className,
    "pointer-events-none fixed z-[9999] m-0 bg-surface opacity-90 shadow-[var(--shadow-menu)]",
  );
  Object.assign(ghost.style, {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${card.offsetWidth || rect.width}px`,
    fontSize: getComputedStyle(card).fontSize,
    transformOrigin: "0 0",
    transform: `scale(${scale})`,
  });
  card.ownerDocument.body.appendChild(ghost);
  return { ghost, scale };
}

/** Pointer handlers that drag a card between the board's columns. */
export function useKanbanCardDrag(
  handlers: KanbanCardDragHandlers,
): KanbanCardDrag {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const draggedRef = useRef(false);
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cancelRef.current?.(), []);

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    const card = event.currentTarget;
    const control =
      event.target instanceof Element
        ? event.target.closest(NESTED_CONTROLS)
        : null;
    if (
      event.button !== 0 ||
      cancelRef.current ||
      (control && control !== card && card.contains(control))
    ) {
      return;
    }
    const doc = card.ownerDocument;
    const { pointerId } = event;
    const start: Point = { clientX: event.clientX, clientY: event.clientY };
    let ghost: HTMLElement | null = null;
    let scale = 1;
    let over: TaskStatus | null = null;
    draggedRef.current = false;

    const move = (moveEvent: globalThis.PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      if (!ghost) {
        const distance = Math.hypot(
          moveEvent.clientX - start.clientX,
          moveEvent.clientY - start.clientY,
        );
        if (distance < DRAG_THRESHOLD_PX) return;
        ({ ghost, scale } = createGhost(card));
        draggedRef.current = true;
        card.setPointerCapture?.(pointerId);
        handlersRef.current.onDragStart();
      }
      moveEvent.preventDefault();
      ghost.style.transform = `translate(${moveEvent.clientX - start.clientX}px, ${moveEvent.clientY - start.clientY}px) scale(${scale})`;
      scrollBoardAtEdge(card, moveEvent);
      const status = statusAt(doc, moveEvent);
      if (status !== over) {
        over = status;
        handlersRef.current.onDragOver(status);
      }
    };
    const end = (point: Point | null) => {
      detach();
      if (!ghost) return;
      ghost.remove();
      handlersRef.current.onDragEnd(point ? statusAt(doc, point) : null);
    };
    const up = (upEvent: globalThis.PointerEvent) => {
      if (upEvent.pointerId === pointerId) end(upEvent);
    };
    const cancel = (cancelEvent: globalThis.PointerEvent) => {
      if (cancelEvent.pointerId === pointerId) end(null);
    };
    const escape = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== "Escape" || !ghost) return;
      keyEvent.preventDefault();
      keyEvent.stopPropagation();
      end(null);
    };
    const detach = () => {
      cancelRef.current = null;
      doc.removeEventListener("pointermove", move);
      doc.removeEventListener("pointerup", up);
      doc.removeEventListener("pointercancel", cancel);
      doc.removeEventListener("keydown", escape, true);
      if (card.hasPointerCapture?.(pointerId)) {
        card.releasePointerCapture(pointerId);
      }
    };
    cancelRef.current = () => end(null);
    doc.addEventListener("pointermove", move);
    doc.addEventListener("pointerup", up);
    doc.addEventListener("pointercancel", cancel);
    doc.addEventListener("keydown", escape, true);
  };

  const consumeClick = () => {
    const dragged = draggedRef.current;
    draggedRef.current = false;
    return dragged;
  };

  return { onPointerDown, consumeClick };
}
